import { Article } from '../types';

export const nercEstimatedBillingCappingOrderRefundGuide: Article = {
    id: 'nerc-estimated-billing-capping-order-refund-guide',
    title: 'NERC Estimated Billing Capping Order: Stop DisCo Overbilling & Reclaim Credits (2026 Guide)',
    slug: 'nerc-estimated-billing-capping-order-refund-guide',
    excerpt: 'Practical 2026 guide to NERC’s estimated billing capping rules: how unmetered customers check feeder caps, dispute overbilling, protect supply during a complaint, and get account credits.',
    category: 'Power Generation',
    date: 'July 27, 2026',
    dateModified: 'July 27, 2026',
    readTime: '9 min read',
    author: 'power.ng Research Team',
    trending: true,
    keywords: [
        'NERC estimated billing capping order',
        'unmetered electricity bill cap Nigeria',
        'how to challenge crazy electricity bill Nigeria',
        'DisCo estimated billing overbilling credit',
        'unmetered electricity bill complaint NERC',
        'Electricity Act 2023 consumer billing rights'
    ],
    metaDescription: 'Use NERC estimated billing caps to stop DisCo overbilling: check your feeder kWh limit, file a written dispute, and claim account credits if you were charged above the cap.',
    quickAnswer: "For unmetered customers hit with inflated DisCo bills, NERC sets a hard legal energy cap: your monthly estimated bill should not exceed the approved cap for metered customers on your feeder. Compare your billed kWh to the published Capping Schedule, and if you were overcharged, file a formal written dispute while paying your last undisputed bill — DisCos should not punish a properly lodged billing complaint with disconnection of the disputed amount.",
    faqs: [
        {
            question: 'What is the NERC Estimated Billing Capping Order?',
            answer: 'It is NERC’s regulatory framework (notably Order No. NERC/197/2020, with later methodology and monthly schedules) that sets a maximum kWh DisCos may bill many unmetered customers. Caps are tied to the average consumption of metered customers on the same feeder or business-unit schedule NERC publishes.'
        },
        {
            question: 'Who is covered by estimated billing caps?',
            answer: 'Caps are aimed primarily at unmetered customers in common residential and small commercial classes (especially R2 and C1). Always check the current monthly schedule for your DisCo, business unit, feeder, and service band, because published caps differ by location and band.'
        },
        {
            question: 'How is my unmetered monthly capping limit calculated?',
            answer: 'NERC issues monthly energy caps using metered-customer consumption data for the relevant feeder or business unit. Your DisCo should not bill an eligible unmetered customer above the published cap for that feeder/band for the billing month.'
        },
        {
            question: 'Can a DisCo disconnect me while an overbilling dispute is pending?',
            answer: 'NERC consumer rights allow an unmetered customer disputing an estimated bill to pay the last undisputed bill while the dispute is processed. Lodge the complaint in writing, keep the ticket or stamped copy, and keep paying the undisputed amount. If supply is cut despite that, escalate immediately to your DisCo complaints unit and the NERC Forum Office.'
        },
        {
            question: 'What happens if my DisCo billed me above the NERC cap for several months?',
            answer: 'NERC has repeatedly ordered DisCos to issue credit adjustments (account credits/write-downs), not cash refunds, for verified overbilling above approved caps. Ask for a full account audit covering every overbilled month and keep written evidence of the claim.'
        }
    ],
    content: `## Who This Guide Is For

This guide is for **unmetered** residential and small commercial customers in Nigeria who receive estimated (“crazy”) bills and want to:

1. Check whether the bill exceeds the **NERC energy cap** for their feeder  
2. File a **written DisCo dispute** with evidence  
3. Escalate to the **NERC Forum Office** if the DisCo ignores the complaint  
4. Seek **account credits** for months billed above the cap  

If you already have a working prepaid meter, use our general [electricity billing dispute guide](/articles/electricity-billing-dispute-resolution) instead.

---

## 1. What NERC’s Estimated Billing Cap Actually Is

For years, unmetered customers of DisCos such as **Ikeja Electric (IKEDC)**, **Eko Electricity (EKEDP)**, **AEDC**, **IBEDC**, **EEDC**, and others faced arbitrary estimated bills.

To limit that practice, the Nigerian Electricity Regulatory Commission (NERC) issued the **Order on the Capping of Estimated Bills** (**Order No. NERC/197/2020**) and later methodology/orders that support **monthly energy caps**. Those rules still matter in **2026**: NERC continues to publish feeder/business-unit schedules and has sanctioned DisCos for billing above approved caps.

> **Golden rule**  
> For eligible unmetered customers, the **kWh on your estimated bill** should not exceed the **published NERC energy cap** for your feeder / service band for that month.

### What the rule is designed to do

- **Align unmetered bills with metered neighbours** on the same supply area/feeder schedule  
- **Stop arbitrary revenue padding** through inflated estimates  
- **Force transparency** via published monthly capping schedules  

> **Scope note:** Caps are especially relevant for common **R2** and **C1** classes. Always confirm your tariff class, feeder name, and the current month’s schedule before you dispute.

---

## 2. Step-by-Step: Audit Your Monthly Bill

Do not pay an unexplained spike without checking the figures.

1. Find billed **Energy Consumed (kWh)** on the invoice  
2. Identify your **feeder / business unit / service band**  
3. Look up the official **NERC monthly energy cap** for that feeder  
4. Calculate any overcharge and prepare a written dispute  

### Step 1: Locate consumption (kWh)

On your paper or digital bill, find **Energy Consumed (kWh)**, **Current Consumption**, or the equivalent line item.

### Step 2: Look up your feeder’s NERC cap

Your feeder name is often listed under account information (for example, *Abule Egba 11kV* or *Gwarinpa 33kV*). Check:

- Your DisCo website / customer portal notices  
- NERC publications of monthly energy caps for your DisCo  

Use the schedule that matches the **billing month**, not an old PDF from a different month.

### Step 3: Compare bill vs. cap

**Formula:** \`Overbilled kWh = Billed kWh − NERC Cap kWh\`

| Billing detail | Example (illustrative only) |
| :--- | :--- |
| **Customer type** | Residential, single-phase, unmetered |
| **Billed energy** | 450 kWh |
| **NERC feeder cap** | 180 kWh |
| **Excess** | **270 kWh above cap** |
| **Money impact** | Excess kWh × your approved tariff rate |

> The naira example depends on your **approved tariff band and DisCo rate** for that month. Recalculate with the rate printed on your bill; do not assume a national flat rate.

If billed kWh is above the published cap for your feeder/band, document it and dispute in writing.

---

## 3. How to Submit a Formal Dispute (With Letter Template)

Verbal complaints to field staff are weak evidence. File a **written** complaint at your DisCo Business Unit / Undertaking office (and through any official app/email channel your DisCo recognises).

> **Demand proof of filing:** Get a **complaint ticket number** or a stamped duplicate. Without that paper trail, escalation to NERC is much harder.

### Sample overbilling dispute letter

> **The Business Unit Manager,**  
> [Name of your DisCo, e.g., Ikeja Electric / AEDC]  
> [Business Unit Office Address], [City, State]  
>  
> **FORMAL DISPUTE AGAINST ESTIMATED BILLING ABOVE NERC ENERGY CAP**  
> **Account number:** [Your Account Number]  
> **Service address:** [Your Full Address]  
> **Feeder / business unit:** [As shown on your bill]  
> **Tariff class / band:** [e.g., R2 / Band B]  
>  
> Dear Sir/Madam,  
>  
> I formally dispute the estimated electricity bill(s) for **[Month(s), Year]** on the account above.  
>  
> My bill states consumption of **[Billed kWh] kWh** totaling **₦[Amount]**. The applicable NERC monthly energy cap for my feeder/service band is **[Cap kWh] kWh**. The invoice therefore exceeds the published cap by **[Excess kWh] kWh**.  
>  
> Under NERC’s estimated billing capping framework and applicable consumer-protection rules, I request:  
> 1. An immediate account audit and downward adjustment to the lawful capped energy;  
> 2. Credit/write-off of accumulated overbilling for every month billed above the approved cap;  
> 3. Priority metering under the applicable MAF / MAP / DISREP process for my feeder.  
>  
> While this dispute is pending, I will continue to pay my **last undisputed bill amount**. Kindly issue a formal complaint reference and written response.  
>  
> Yours faithfully,  
> **[Full Name]**  
> [Phone] | [Email]

---

## 4. Escalating to the NERC Forum Office

If your DisCo ignores the letter, refuses adjustment, or fails to resolve the complaint within the DisCo’s published resolution window, escalate to the **NERC Forum Office** that covers your DisCo area.

> **Practical escalation path**  
> 1. File written complaint with DisCo and keep the ticket/stamp  
> 2. Follow up in writing and keep paying the **last undisputed** amount  
> 3. If unresolved, file at the regional **NERC Forum Office** with your evidence pack  
> 4. Seek an order for account audit and credit adjustment

### Evidence pack to attach

- Disputed bill(s) and recent previous bills  
- The NERC/DisCo capping schedule page for your feeder and month  
- Stamped complaint letter or ticket number  
- Proof of payments made on the undisputed amount  
- Any SMS/email/app complaint references  

Forum offices operate across DisCo coverage areas (including major centres such as Lagos, Abuja, Ibadan, and Port Harcourt). Confirm the correct office for your service address before filing.

When NERC finds capping violations, the usual customer remedy is an **account credit / bill adjustment**, consistent with past NERC enforcement orders directing DisCos to credit overbilled unmetered customers.

---

## 5. Your Rights While a Billing Dispute Is Open

DisCos sometimes use disconnection threats to force payment of disputed estimates. Know the consumer-protection baseline:

> **While disputing an estimated bill:** NERC’s published consumer rights allow an unmetered customer to **pay the last undisputed bill** while the contested bill goes through the dispute process. Keep proof that the dispute was filed and that undisputed payments continued.

If technicians attempt disconnection despite an open written dispute and continued payment of the undisputed amount:

1. Show the stamped complaint / ticket and payment proof  
2. Record names, time, and feeder/account details  
3. Report immediately to DisCo customer care and escalate to the **NERC Forum Office**  
4. Ask for written confirmation that the disputed amount is under investigation  

Do not rely on verbal assurances alone—paper trails win these cases.

---

## Key Takeaways

1. NERC energy caps still apply in 2026; check the **current month’s schedule** for your feeder and band.  
2. Compare **billed kWh vs. capped kWh** before you pay a spike.  
3. Dispute **in writing**, keep the ticket, and request **account credits** for overbilled months.  
4. During the dispute, pay the **last undisputed bill** and document everything.  
5. If the DisCo stalls, escalate to the **NERC Forum Office** with a complete evidence pack.  
6. The long-term fix is a working meter—track MAF / DISREP / MAP options in parallel.

---

## Related Guides & Tools

- [Electricity Billing Dispute Resolution in Nigeria](/articles/electricity-billing-dispute-resolution)
- [How to Get a Free Prepaid Meter (MAF & DISREP)](/articles/how-to-get-free-prepaid-meter-nigeria-maf-disrep)
- [Electricity Act 2023 Explained](/articles/electricity-act-2023-summary-explained)
- [How to File a NERC Complaint](/articles/how-to-file-nerc-complaint)
- [Free Electricity Bill Calculator](/tools/bill-calculator)`
};
